/**
 * Generic Airtable storage adapter
 * Implements the generic StorageAdapter interface for Airtable
 */

import Airtable from 'airtable';
import { 
  StorageAdapter,
  StorageConfig,
  StorageRecord, 
  StorageFilter,
  StorageError,
  CollectionMetadata
} from '../interfaces/storage';
import { defaultLogger } from '../utils';

export interface AirtableConfig extends StorageConfig {
  apiKey: string;
  baseId: string;
  collections?: Record<string, string>; // collection name -> table name mapping
  fieldMappings?: Record<string, Record<string, string>>; // collection -> field mappings
}

export class AirtableStorageAdapter implements StorageAdapter {
  private base: Airtable.Base | null = null;
  private config: AirtableConfig | null = null;
  private collections: Map<string, string> = new Map();
  private fieldMappings: Map<string, Record<string, string>> = new Map();

  async initialize(config: StorageConfig): Promise<void> {
    const airtableConfig = config as AirtableConfig;
    
    if (!airtableConfig.apiKey || !airtableConfig.baseId) {
      throw new StorageError(
        'Airtable adapter requires apiKey and baseId',
        'INVALID_CONFIG'
      );
    }

    Airtable.configure({
      apiKey: airtableConfig.apiKey,
    });

    this.base = Airtable.base(airtableConfig.baseId);
    this.config = airtableConfig;

    // Set up collection mappings
    if (airtableConfig.collections) {
      Object.entries(airtableConfig.collections).forEach(([collection, table]) => {
        this.collections.set(collection, table);
      });
    }

    // Set up field mappings
    if (airtableConfig.fieldMappings) {
      Object.entries(airtableConfig.fieldMappings).forEach(([collection, mapping]) => {
        this.fieldMappings.set(collection, mapping);
      });
    }

    defaultLogger.info('Airtable adapter initialized', { 
      baseId: airtableConfig.baseId.substring(0, 8) + '...',
      collections: Array.from(this.collections.keys())
    });
  }

  async create(collection: string, data: any): Promise<StorageRecord> {
    if (!this.base) {
      throw new StorageError('Airtable adapter not initialized', 'NOT_INITIALIZED');
    }

    const tableName = this.getTableName(collection);
    const fields = this.transformFields(collection, data);

    try {
      const records = await this.base(tableName).create([{ fields }] as any);
      const record = (records as any)[0];

      defaultLogger.info('Record created in Airtable', { 
        recordId: record.id,
        collection,
        table: tableName
      });

      return {
        id: record.id,
        data: this.reverseTransformFields(collection, record.fields),
        metadata: {
          createdAt: new Date(),
          tableName
        }
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      defaultLogger.error('Failed to create Airtable record', { 
        error: errorMessage, 
        collection,
        table: tableName 
      });
      throw new StorageError(
        `Failed to create record: ${errorMessage}`,
        'CREATE_FAILED',
        { collection, tableName }
      );
    }
  }

  async read(collection: string, id: string): Promise<StorageRecord | null> {
    if (!this.base) {
      throw new StorageError('Airtable adapter not initialized', 'NOT_INITIALIZED');
    }

    const tableName = this.getTableName(collection);

    try {
      const record = await this.base(tableName).find(id);
      
      return {
        id: record.id,
        data: this.reverseTransformFields(collection, record.fields),
        metadata: {
          createdAt: new Date(),
          tableName
        }
      };
    } catch (error) {
      if (error instanceof Error && error.message.includes('NOT_FOUND')) {
        return null;
      }
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new StorageError(
        `Failed to read record: ${errorMessage}`,
        'READ_FAILED',
        { collection, id }
      );
    }
  }

  async update(collection: string, id: string, data: any): Promise<StorageRecord> {
    if (!this.base) {
      throw new StorageError('Airtable adapter not initialized', 'NOT_INITIALIZED');
    }

    const tableName = this.getTableName(collection);
    const fields = this.transformFields(collection, data);

    try {
      const records = await this.base(tableName).update([
        { id, fields }
      ] as any);
      const record = (records as any)[0];

      defaultLogger.info('Record updated in Airtable', { 
        recordId: record.id,
        collection,
        table: tableName
      });

      return {
        id: record.id,
        data: this.reverseTransformFields(collection, record.fields),
        metadata: {
          createdAt: new Date(),
          tableName
        }
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new StorageError(
        `Failed to update record: ${errorMessage}`,
        'UPDATE_FAILED',
        { collection, id }
      );
    }
  }

  async delete(collection: string, id: string): Promise<void> {
    if (!this.base) {
      throw new StorageError('Airtable adapter not initialized', 'NOT_INITIALIZED');
    }

    const tableName = this.getTableName(collection);

    try {
      await this.base(tableName).destroy([id] as any);
      
      defaultLogger.info('Record deleted from Airtable', { 
        recordId: id,
        collection,
        table: tableName
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new StorageError(
        `Failed to delete record: ${errorMessage}`,
        'DELETE_FAILED',
        { collection, id }
      );
    }
  }

  async query(collection: string, filter: StorageFilter): Promise<StorageRecord[]> {
    if (!this.base) {
      throw new StorageError('Airtable adapter not initialized', 'NOT_INITIALIZED');
    }

    const tableName = this.getTableName(collection);

    try {
      const selectOptions: any = {
        maxRecords: filter.limit || 100
      };

      if (filter.offset) {
        selectOptions.pageSize = filter.limit || 100;
      }

      if (filter.orderBy) {
        selectOptions.sort = filter.orderBy.map(order => ({
          field: this.getFieldName(collection, order.field),
          direction: order.direction
        }));
      }

      if (filter.where) {
        selectOptions.filterByFormula = this.buildAirtableFormula(collection, filter.where);
      }

      const records = await this.base(tableName).select(selectOptions).all();

      defaultLogger.info('Queried records from Airtable', { 
        count: records.length,
        collection,
        table: tableName
      });

      return records.map(record => ({
        id: record.id,
        data: this.reverseTransformFields(collection, record.fields),
        metadata: {
          createdAt: new Date(),
          tableName
        }
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new StorageError(
        `Failed to query records: ${errorMessage}`,
        'QUERY_FAILED',
        { collection }
      );
    }
  }

  async batchCreate(collection: string, data: any[]): Promise<StorageRecord[]> {
    if (!this.base) {
      throw new StorageError('Airtable adapter not initialized', 'NOT_INITIALIZED');
    }

    const tableName = this.getTableName(collection);
    const recordsToCreate = data.map(item => ({
      fields: this.transformFields(collection, item)
    }));

    try {
      // Airtable has a limit of 10 records per create request
      const results: any[] = [];
      for (let i = 0; i < recordsToCreate.length; i += 10) {
        const batch = recordsToCreate.slice(i, i + 10);
        const created = await this.base(tableName).create(batch as any);
        results.push(...(created as any));
      }

      defaultLogger.info('Batch created records in Airtable', { 
        count: results.length,
        collection,
        table: tableName
      });

      return results.map(record => ({
        id: record.id,
        data: this.reverseTransformFields(collection, record.fields),
        metadata: {
          createdAt: new Date(),
          tableName
        }
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new StorageError(
        `Failed to batch create records: ${errorMessage}`,
        'BATCH_CREATE_FAILED',
        { collection }
      );
    }
  }

  async getCollectionMetadata(collection: string): Promise<CollectionMetadata> {
    const tableName = this.getTableName(collection);
    
    // Note: Airtable API doesn't provide a direct way to get table metadata
    // This would need to be extended with the Metadata API when available
    return {
      name: tableName,
      // Additional metadata would require Airtable Metadata API
    };
  }

  private getTableName(collection: string): string {
    return this.collections.get(collection) || collection;
  }

  private getFieldName(collection: string, field: string): string {
    const mapping = this.fieldMappings.get(collection);
    return mapping?.[field] || field;
  }

  private transformFields(collection: string, data: any): Record<string, any> {
    const mapping = this.fieldMappings.get(collection);
    if (!mapping) {
      return data;
    }

    const transformed: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
      const fieldName = mapping[key] || key;
      transformed[fieldName] = value;
    }
    return transformed;
  }

  private reverseTransformFields(collection: string, fields: any): Record<string, any> {
    const mapping = this.fieldMappings.get(collection);
    if (!mapping) {
      return fields;
    }

    const reversed: Record<string, any> = {};
    const reverseMapping = Object.entries(mapping).reduce((acc, [key, value]) => {
      acc[value] = key;
      return acc;
    }, {} as Record<string, string>);

    for (const [fieldName, value] of Object.entries(fields)) {
      const key = reverseMapping[fieldName] || fieldName;
      reversed[key] = value;
    }
    return reversed;
  }

  private buildAirtableFormula(collection: string, where: Record<string, any>): string {
    const conditions: string[] = [];
    
    for (const [key, value] of Object.entries(where)) {
      const fieldName = this.getFieldName(collection, key);
      
      if (value === null) {
        conditions.push(`{${fieldName}} = BLANK()`);
      } else if (typeof value === 'string') {
        conditions.push(`{${fieldName}} = '${value.replace(/'/g, "\\'")}'`);
      } else if (typeof value === 'number') {
        conditions.push(`{${fieldName}} = ${value}`);
      } else if (typeof value === 'boolean') {
        conditions.push(`{${fieldName}} = ${value ? 'TRUE()' : 'FALSE()'}`);
      } else if (value instanceof Date) {
        conditions.push(`{${fieldName}} = '${value.toISOString()}'`);
      }
    }

    return conditions.length > 0 ? `AND(${conditions.join(', ')})` : '';
  }
}

/**
 * Factory function to create an Airtable storage adapter
 */
export function createAirtableAdapter(config: AirtableConfig): AirtableStorageAdapter {
  return new AirtableStorageAdapter();
}